import styled from "styled-components";
import { GRIFFLE_TYPE, GRIFFLE_RADIUS } from "../griffleTheme";

export const StyledGriffleLogin = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  font-family: var(--font-heading);

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ── Full-page green letter-pattern background ── */
  .login-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  /* ── White organic blob — SVG image from Figma ── */
  .login-blob {
    position: absolute;
    left: -18%;
    top: -16%;
    width: 77%;
    height: 132%;
    z-index: 1;
    pointer-events: none;

    img {
      width: 100%;
      height: 100%;
      object-fit: fill;
    }
  }

  /* ── Form content positioned within the blob ── */
  .login-content {
    position: absolute;
    left: 0;
    top: 0;
    width: 44%;
    height: 100%;
    z-index: 2;
    padding: 48px 56px 48px 88px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  /* ── GRIFFLE logo ─────────────────────────────── */
  .login-logo {
    font-family: var(--font-heading);
    font-weight: ${GRIFFLE_TYPE.logoWeight};
    font-size: 52px;
    color: #e5face;
    -webkit-text-stroke: 3.5px #152b08;
    paint-order: stroke fill;
    text-shadow: 2px 3px 0 #152b08, 3px 5px 0 rgba(21, 43, 8, 0.55);
    letter-spacing: 4px;
    line-height: 1;
    margin-bottom: 40px;
    display: block;
    position: absolute;
    top: 40px;
    left: 88px;
  }

  /* ── Welcome heading ──────────────────────────── */
  .login-heading {
    font-family: var(--font-heading);
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    font-size: 35px;
    color: #152b08;
    line-height: 1.3;
    margin-bottom: 32px;
    max-width: 340px;
    text-align: center;
  }

  /* ── Input fields ─────────────────────────────── */
  .login-field {
    position: relative;
    margin-bottom: 16px;
  }

  .login-input {
    width: 100%;
    height: 55px;
    border: 2px solid rgba(21, 43, 8, 0.38);
    border-radius: 27.5px;
    padding: 0 20px 0 52px;
    font-family: var(--font-heading);
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    font-size: 20px;
    letter-spacing: 1px;
    color: #152b08;
    background: #fff;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s;

    &::placeholder {
      color: rgba(21, 43, 8, 0.38);
      letter-spacing: 1px;
    }

    &:focus {
      border-color: var(--color-primary);
    }
  }

  .input-icon {
    position: absolute;
    left: 18px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    opacity: 0.5;
    pointer-events: none;
  }

  .field-error {
    font-family: var(--font-heading);
    font-size: 13px;
    color: #e53935;
    margin: 4px 0 0 22px;
  }

  /* ── Remember me ──────────────────────────────── */
  .login-remember {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 4px 0 28px 4px;
    cursor: pointer;
    font-family: var(--font-heading);
    font-size: 20px;
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    color: #152b08;
    user-select: none;

    input[type="checkbox"] {
      appearance: none;
      -webkit-appearance: none;
      width: 24px;
      height: 24px;
      border: 2px solid var(--color-primary);
      border-radius: 6px;
      background: #fff;
      cursor: pointer;
      position: relative;
      flex-shrink: 0;
      transition: background 0.15s;

      &:checked {
        background: var(--color-primary);

        &::after {
          content: "";
          position: absolute;
          inset: 3px;
          background: url("data:image/svg+xml,%3Csvg viewBox='0 0 12 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 5l3.5 4L11 1' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
            no-repeat center / contain;
        }
      }
    }
  }

  /* ── Button row ───────────────────────────────── */
  .login-actions {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  .login-btn {
    height: 62px;
    width: 252px;
    padding: 0;
    background: #539a18;
    border: 3px solid #fff;
    border-radius: ${GRIFFLE_RADIUS.pillLg};
    box-shadow: 0px 4px 18px 0px rgba(0, 0, 0, 0.25);
    cursor: pointer;
    font-family: var(--font-heading);
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    font-size: 28px;
    letter-spacing: 2px;
    color: #fff;
    transition: opacity 0.15s, transform 0.1s;

    &:hover {
      opacity: 0.92;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  .form-error {
    font-family: var(--font-heading);
    font-size: 13px;
    color: #e53935;
    margin: 6px 0 4px;
  }

  .login-loader {
    display: flex;
    justify-content: center;
    margin: 8px 0;
  }

  /* ── Mascot — single character, right side, centered ── */
  .login-mascot {
    position: absolute;
    right: 8%;
    top: 50%;
    transform: translateY(-50%);
    width: 28%;
    max-width: 380px;
    z-index: 3;
    pointer-events: none;

    img {
      display: block;
      width: 100%;
      height: auto;
      filter: drop-shadow(0px 1px 52px #0e2c01);
    }
  }

  /* ── Cloud decoration — upper right corner ─────── */
  .login-cloud {
    position: absolute;
    top: -40px;
    right: -60px;
    z-index: 4;
    width: 200px;
    pointer-events: none;

    img {
      width: 100%;
      height: auto;
      display: block;
    }
  }

  /* ── Tablet (≤1024px) ─────────────────────────── */
  @media (max-width: 1024px) {
    .login-blob {
      left: -22%;
      width: 84%;
    }

    .login-content {
      width: 50%;
      padding: 48px 40px 48px 60px;
    }

    .login-logo {
      left: 60px;
    }

    .login-mascot {
      width: 36%;
      right: 2%;
    }
  }

  /* ── Mobile (≤768px): stacked layout ─────────── */
  @media (max-width: 768px) {
    overflow-y: auto;
    height: auto;
    min-height: 100vh;

    .login-blob {
      display: none;
    }

    .login-content {
      position: relative;
      width: 100%;
      min-height: 100vh;
      padding: 100px 28px 200px;
      background: rgba(255, 255, 255, 0.94);
      backdrop-filter: blur(6px);
      z-index: 2;
      justify-content: flex-start;
    }

    .login-logo {
      left: 28px;
      font-size: 40px;
    }

    .login-heading {
      font-size: 26px;
      max-width: 100%;
    }

    .login-mascot {
      position: fixed;
      right: 0;
      bottom: 0;
      top: auto;
      transform: none;
      width: 160px;
      max-width: 160px;
      z-index: 3;

      img {
        width: 100%;
        margin-left: 0;
      }
    }
  }

  /* ── Small phones (≤480px) ────────────────────── */
  @media (max-width: 480px) {
    .login-content {
      padding: 100px 20px 220px;
    }

    .login-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .login-btn {
      width: 100%;
      text-align: center;
    }
  }
`;
